import { useEffect, useRef, useState } from "react";
import { contentToHtml, contentToPlainText, draftToHtml, draftToPlainText } from "../shared/content";
import { platformLabel, targetLabel } from "../shared/platform";
import type { AnalysisResult, ExtractedContent, GeneratedDraft, Platform } from "../shared/types";

type BusyAction = "extract" | "analyze" | "draft" | null;

function isImportedContent(value: unknown): value is ExtractedContent {
  if (!value || typeof value !== "object") return false;
  const content = value as Partial<ExtractedContent>;
  if (typeof content.sourceUrl !== "string" || !/^https?:\/\//i.test(content.sourceUrl)) return false;
  if (!Array.isArray(content.textBlocks) || !Array.isArray(content.images)) return false;
  if (content.platform !== "xiaohongshu" && content.platform !== "xianyu" && content.platform !== "generic") return false;
  return content.images.every((image) => image && typeof image.url === "string" && typeof image.order === "number");
}

const XIANYU_PAGE_CHROME = new Set(["担保交易", "包邮", "展开", "收起", "立即购买", "聊一聊", "收藏", "举报", "分享"]);

function isPageChrome(value: string, platform: ExtractedContent["platform"]): boolean {
  if (platform !== "xianyu") return false;
  const normalized = value.replace(/\s+/g, " ").trim();
  return XIANYU_PAGE_CHROME.has(normalized) || /^\d{1,8}$/.test(normalized) || /^\d+(?:人想要|浏览)$/.test(normalized);
}

function sanitizeContent(value: ExtractedContent): ExtractedContent {
  const seen = new Set<string>();
  const textBlocks = value.textBlocks
    .map((block) => block.replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim())
    .filter((block) => block.length >= 2 && !isPageChrome(block, value.platform) && !seen.has(block) && seen.add(block));
  return { ...value, textBlocks };
}

const platformOptions: Array<{ value: Platform; label: string }> = [
  { value: "auto", label: "自动识别" },
  { value: "xiaohongshu", label: "小红书" },
  { value: "xianyu", label: "闲鱼" },
  { value: "generic", label: "通用网页" },
];

async function api<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: body ? "POST" : "GET",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = (await response.json().catch(() => ({}))) as { data?: T; error?: string };
  if (!response.ok) throw new Error(payload.error || "请求失败，请稍后重试。");
  return payload.data as T;
}

function imageSrc(url: string): string {
  return `/api/media?url=${encodeURIComponent(url)}`;
}

function ProgressIndicator({ value, label }: { value: number; label: string }) {
  return (
    <div className="progress-indicator" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value}>
      <div className="progress-meta"><span>{label}</span><strong>{value}%</strong></div>
      <div className="progress-track"><div className="progress-fill" style={{ width: `${value}%` }} /></div>
    </div>
  );
}

async function copyRichText(html: string, plainText: string): Promise<void> {
  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    const item = new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([plainText], { type: "text/plain" }),
    });
    await navigator.clipboard.write([item]);
    return;
  }
  await navigator.clipboard.writeText(plainText);
}

function App() {
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState<Platform>("auto");
  const [content, setContent] = useState<ExtractedContent | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [draft, setDraft] = useState<GeneratedDraft | null>(null);
  const [target, setTarget] = useState<"xiaohongshu" | "xianyu">("xiaohongshu");
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<string[]>([]);
  const [busy, setBusy] = useState<BusyAction>(null);
  const [progress, setProgress] = useState(0);
  const [progressAction, setProgressAction] = useState<BusyAction>(null);
  const [notice, setNotice] = useState<{ tone: "success" | "error" | "info"; text: string } | null>(null);
  const lastExtensionNonce = useRef<string | undefined>(undefined);
  const progressTimer = useRef<number | null>(null);
  const progressResetTimer = useRef<number | null>(null);

  const canAnalyze = Boolean(content && (content.platform === "generic" || content.platform === "xiaohongshu" || content.platform === "xianyu"));

  function showNotice(text: string, tone: "success" | "error" | "info" = "info") {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4200);
  }

  function startBusy(action: Exclude<BusyAction, null>) {
    if (progressTimer.current !== null) window.clearInterval(progressTimer.current);
    if (progressResetTimer.current !== null) window.clearTimeout(progressResetTimer.current);
    setBusy(action);
    setProgressAction(action);
    setProgress(0);
    progressTimer.current = window.setInterval(() => setProgress((current) => (current >= 99 ? 99 : current + 1)), 70);
  }

  function finishBusy() {
    if (progressTimer.current !== null) {
      window.clearInterval(progressTimer.current);
      progressTimer.current = null;
    }
    setProgress(100);
    setBusy(null);
    progressResetTimer.current = window.setTimeout(() => {
      setProgress(0);
      setProgressAction(null);
      progressResetTimer.current = null;
    }, 700);
  }

  useEffect(() => () => {
    if (progressTimer.current !== null) window.clearInterval(progressTimer.current);
    if (progressResetTimer.current !== null) window.clearTimeout(progressResetTimer.current);
  }, []);

  useEffect(() => {
    const handleExtensionMessage = (event: MessageEvent) => {
      if (event.source !== window || event.data?.source !== "linkcopy-extension") return;
      if (!isImportedContent(event.data.content)) return;
      if (event.data.nonce && event.data.nonce === lastExtensionNonce.current) return;
      lastExtensionNonce.current = event.data.nonce;
      const imported = sanitizeContent(event.data.content);
      setUrl(imported.sourceUrl);
      setPlatform(imported.platform);
      setContent(imported);
      setAnalysis(null);
      setDraft(null);
      setTarget(imported.platform === "xianyu" ? "xianyu" : "xiaohongshu");
      showNotice(`已从浏览器扩展导入 ${imported.textBlocks.length} 段文字和 ${imported.images.length} 张图片。`, "success");
    };
    window.addEventListener("message", handleExtensionMessage);
    return () => window.removeEventListener("message", handleExtensionMessage);
  }, []);

  async function handleExtract() {
    setNotice(null);
    setAnalysis(null);
    setDraft(null);
    if (!url.trim()) {
      showNotice("先粘贴一个公开链接。", "error");
      return;
    }
    startBusy("extract");
    try {
      const result = await api<{ sourceUrl: string; platform: Exclude<Platform, "auto">; title?: string; author?: string; publishedAt?: string; textBlocks: string[]; images: ExtractedContent["images"] }>("/api/extract", { url, platform });
      const sanitized = sanitizeContent(result);
      setContent(sanitized);
      const nextTarget = result.platform === "xianyu" ? "xianyu" : "xiaohongshu";
      setTarget(nextTarget);
      showNotice(`已提取 ${sanitized.textBlocks.length} 段文字和 ${sanitized.images.length} 张图片。`, "success");
    } catch (error) {
      showNotice(error instanceof Error ? error.message : "页面提取失败。", "error");
    } finally {
      finishBusy();
    }
  }

  async function handleAnalyze() {
    if (!content || !canAnalyze) return;
    startBusy("analyze");
    try {
      const result = await api<AnalysisResult>("/api/analyze", { content, target });
      setAnalysis(result);
      setSelectedSuggestionIds(result.suggestions.map((suggestion) => suggestion.id));
      showNotice(result.fallback ? "AI 中转暂时不可用，已使用本地规则完成分析。" : "分析完成，可以选择建议并生成原创草稿。", result.fallback ? "info" : "success");
    } catch (error) {
      showNotice(error instanceof Error ? error.message : "内容分析失败。", "error");
    } finally {
      finishBusy();
    }
  }

  async function handleDraft() {
    if (!content || !analysis) return;
    startBusy("draft");
    try {
      const result = await api<GeneratedDraft>("/api/draft", { content, target, analysis, selectedSuggestionIds });
      setDraft(result);
      showNotice(result.fallback ? "AI 中转暂时不可用，已生成基础原创草稿。" : "原创草稿已生成，可以继续编辑。", result.fallback ? "info" : "success");
    } catch (error) {
      showNotice(error instanceof Error ? error.message : "原创草稿生成失败。", "error");
    } finally {
      finishBusy();
    }
  }

  async function handleCopyOriginal() {
    if (!content) return;
    try {
      await copyRichText(contentToHtml({ ...content, images: content.images.map((image) => ({ ...image, url: imageSrc(image.url) })) }), contentToPlainText(content));
      showNotice("原始内容已复制，可粘贴到 Word、Notion 或飞书。", "success");
    } catch {
      showNotice("浏览器拒绝了剪贴板操作，请允许剪贴板权限后重试。", "error");
    }
  }

  async function handleCopyDraft(mode: "rich" | "markdown") {
    if (!draft) return;
    const images = draft.images.map((image) => ({ ...image, url: imageSrc(image.url) }));
    try {
      if (mode === "rich") {
        await copyRichText(draftToHtml(draft.title, draft.sections, images, draft.sourceUrl), draftToPlainText(draft.title, draft.sections, draft.sourceUrl));
      } else {
        await navigator.clipboard.writeText(draftToPlainText(draft.title, draft.sections, draft.sourceUrl));
      }
      showNotice(mode === "rich" ? "原创富文本已复制。" : "Markdown 文本已复制。", "success");
    } catch {
      showNotice("剪贴板操作失败，请检查浏览器权限。", "error");
    }
  }

  function toggleSuggestion(id: string) {
    setSelectedSuggestionIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  }

  function updateDraftBody(value: string) {
    setDraft((current) => (current ? { ...current, sections: value.split(/\n{2,}/) } : current));
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">↗</span>
          <span className="brand-name">Link<span>Copy</span></span>
          <span className="brand-divider">/</span>
          <span className="brand-subtitle">原创内容工作台</span>
        </div>
        <span className="privacy-note">公开链接 · 当前页面处理</span>
      </header>

      <section className="workspace-grid">
        <div className="input-column">
          <div className="section-heading">
            <span className="step-number">01</span>
            <div><h2>粘贴公开链接</h2><p>支持小红书笔记、闲鱼商品和通用网页</p></div>
          </div>

          <div className="input-panel">
            <label htmlFor="source-url">链接地址</label>
            <div className="url-row">
              <input id="source-url" value={url} onChange={(event) => setUrl(event.target.value)} onKeyDown={(event) => event.key === "Enter" && void handleExtract()} placeholder="https://..." spellCheck={false} />
              <button className="icon-button" type="button" onClick={() => setUrl("")} disabled={!url} aria-label="清空链接">×</button>
            </div>
            <div className="platform-picker" role="group" aria-label="选择平台">
              {platformOptions.map((option) => <button key={option.value} className={platform === option.value ? "platform-chip active" : "platform-chip"} type="button" onClick={() => setPlatform(option.value)}>{option.label}</button>)}
            </div>
            <button className="primary-button" type="button" onClick={() => void handleExtract()} disabled={busy !== null}>{busy === "extract" ? "正在提取…" : "开始提取"}<span>↗</span></button>
            {progressAction === "extract" && <ProgressIndicator value={progress} label="正在提取" />}
            <div className="helper-row"><span className="status-dot" />不保存账号信息 · 不绕过登录和验证码</div>
            <p className="extension-hint">已安装扩展后，在闲鱼或小红书页面点击 LinkCopy，即可导入当前页面。</p>
          </div>

          {content && (
            <div className="original-panel">
              <div className="section-heading compact"><span className="step-number">02</span><div><h2>原始内容</h2><p>{platformLabel[content.platform]} · {content.textBlocks.length} 段文字 · {content.images.length} 张图片</p></div></div>
              <div className="source-card"><div className="source-tag">{platformLabel[content.platform]}</div><h3>{content.title || "未检测到标题"}</h3>{content.author && <p className="muted">作者：{content.author}</p>}<p className="source-url">{content.sourceUrl}</p></div>
              <div className="preview-text">{content.textBlocks.map((block, index) => <p key={`${block}-${index}`}>{block}</p>)}</div>
              {content.images.length > 0 && <div className="image-strip">{content.images.map((image) => <img key={`${image.url}-${image.order}`} src={imageSrc(image.url)} alt={image.alt || `原图 ${image.order + 1}`} loading="lazy" />)}</div>}
              <button className="secondary-button full" type="button" onClick={() => void handleCopyOriginal()}>复制原始内容 <span>⌘ C</span></button>
            </div>
          )}
        </div>

        <div className="result-column">
          <div className="section-heading">
            <span className="step-number">03</span>
            <div><h2>分析与原创</h2><p>选择目标平台，生成可编辑草稿</p></div>
          </div>

          <div className="analysis-panel">
            {!content && <div className="empty-state"><div className="empty-orbit">✦</div><h3>提取结果会出现在这里</h3><p>粘贴公开链接后，你可以先复制原始内容，再开始分析和改写。</p></div>}
            {content && !analysis && <div className="ready-state"><div className="ready-icon">02</div><h3>原始内容已准备好</h3><p>选择原创目标后，系统会从标题、结构、表达和图片编排四个方面给出建议。</p><div className="target-tabs"><button className={target === "xiaohongshu" ? "target-tab active" : "target-tab"} type="button" onClick={() => setTarget("xiaohongshu")}>小红书图文</button><button className={target === "xianyu" ? "target-tab active" : "target-tab"} type="button" onClick={() => setTarget("xianyu")}>闲鱼商品</button></div><button className="primary-button" type="button" onClick={() => void handleAnalyze()} disabled={!canAnalyze || busy !== null}>{busy === "analyze" ? "正在分析…" : `分析并给出修改建议`}<span>↗</span></button>{progressAction === "analyze" && <ProgressIndicator value={progress} label="正在分析" />}{!canAnalyze && <p className="inline-warning">当前页面没有可分析的公开内容，请更换链接后重试。</p>}</div>}
            {analysis && <>
              {progressAction === "analyze" && <ProgressIndicator value={progress} label="分析完成" />}
              <div className="analysis-toolbar"><div><span className="mini-label">目标平台</span><strong>{targetLabel(target)}</strong></div><button className="text-button" type="button" onClick={() => setAnalysis(null)}>重新分析</button></div>
              <div className="insight-grid"><div><span className="insight-label">内容优势</span>{analysis.strengths.map((item) => <p className="insight-item positive" key={item}>＋ {item}</p>)}</div><div><span className="insight-label">可以调整</span>{analysis.issues.length ? analysis.issues.map((item) => <p className="insight-item" key={item}>↳ {item}</p>) : <p className="insight-item positive">＋ 暂未发现明显问题</p>}</div></div>
              <div className="suggestions-block"><div className="subheading"><span>修改建议</span><small>选择要应用到原创稿的建议</small></div>{analysis.suggestions.map((suggestion) => <label className="suggestion-row" key={suggestion.id}><input type="checkbox" checked={selectedSuggestionIds.includes(suggestion.id)} onChange={() => toggleSuggestion(suggestion.id)} /><span className="checkbox-mark" /><span className="suggestion-copy"><strong>{suggestion.category === "title" ? "标题" : suggestion.category === "structure" ? "结构" : suggestion.category === "platform" ? "平台" : "表达"}</strong><span>{suggestion.explanation}</span>{suggestion.replacement && <em>示例：{suggestion.replacement}</em>}</span></label>)}</div>
              <div className="image-suggestions"><div className="subheading"><span>配图建议</span><small>原图会继续保留</small></div>{analysis.imageSuggestions.map((item) => <div className="image-suggestion" key={`${item.order}-${item.description}`}><span className="image-index">{String(item.order + 1).padStart(2, "0")}</span><div><strong>{item.description}</strong><p>{item.reason}</p></div></div>)}</div>
              <button className="primary-button" type="button" onClick={() => void handleDraft()} disabled={busy !== null}>{busy === "draft" ? "正在生成草稿…" : "生成可编辑原创草稿"}<span>↗</span></button>
              {progressAction === "draft" && <ProgressIndicator value={progress} label="正在生成草稿" />}
            </>}
          </div>

          {draft && <div className="draft-panel"><div className="draft-heading"><div><span className="mini-label">可编辑草稿 · {targetLabel(draft.platform)}</span><h2>把它改成你的表达</h2></div><span className="draft-status">DRAFT</span></div><label className="edit-label" htmlFor="draft-title">标题</label><input id="draft-title" className="draft-title-input" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /><label className="edit-label" htmlFor="draft-body">正文</label><textarea id="draft-body" className="draft-body-input" value={draft.sections.join("\n\n")} onChange={(event) => updateDraftBody(event.target.value)} rows={16} aria-label="正文" /><div className="draft-actions"><button className="secondary-button" type="button" onClick={() => void handleCopyDraft("rich")}>复制富文本</button><button className="ghost-button" type="button" onClick={() => void handleCopyDraft("markdown")}>复制 Markdown</button></div></div>}
        </div>
      </section>

      <footer className="footer"><span>LINKCOPY / 0.1</span><span>原始内容保留来源链接 · 原创稿由你最终确认</span></footer>
      {notice && <div className={`toast ${notice.tone}`} role="status">{notice.text}</div>}
    </main>
  );
}

export default App;
